<template>
  <div class="rich-editor" :class="{ disabled }">
    <div class="rich-editor-modebar">
      <button
        type="button"
        class="mode-btn"
        :class="{ active: editMode === 'visual' }"
        @click="setEditMode('visual')"
      >
        可视化
      </button>
      <button
        type="button"
        class="mode-btn"
        :class="{ active: editMode === 'source' }"
        @click="setEditMode('source')"
      >
        HTML 源码
      </button>
      <span v-if="editMode === 'source'" class="mode-hint">
        与工具栏「源码」相同；135/秀米 HTML 可在此粘贴后切回可视化
      </span>
    </div>

    <textarea
      v-if="editMode === 'source'"
      v-model="sourceHtml"
      class="rich-source"
      :placeholder="placeholder"
      :disabled="disabled"
      spellcheck="false"
      @input="onSourceInput"
    />

    <Editor
      v-else-if="visualReady"
      :key="editorMountKey"
      :id="editorUid"
      license-key="gpl"
      tinymce-script-src="/tinymce/tinymce.min.js"
      :model-value="editorHtml"
      :disabled="disabled"
      :init="tinyInit"
      @update:model-value="onTinyUpdate"
      @init="onTinyInit"
    />

    <div v-else class="rich-editor-loading">编辑器加载中…</div>
  </div>
</template>

<script>
import Editor from '@tinymce/tinymce-vue'
import { BASE_URL, uploadImage } from '@/api'
import {
  normalizeRichHtmlBody,
  normalizeRichHtmlForEditor,
  normalizeRichHtmlImages,
} from '@/utils/richHtmlNormalize'

function createEditorUid() {
  return `rte-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export default {
  name: 'RichTextEditor',
  components: { Editor },
  props: {
    modelValue: { type: String, default: '' },
    placeholder: { type: String, default: '请输入图文内容…' },
    disabled: { type: Boolean, default: false },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      editMode: 'visual',
      visualReady: false,
      editorMountKey: 0,
      sourceHtml: '',
      editorHtml: '',
      editorUid: createEditorUid(),
      tinyRef: null,
      tinyBootstrapped: false,
      tinyInit: null,
    }
  },
  created() {
    this.tinyInit = this.buildTinyInit()
  },
  mounted() {
    const prepared = normalizeRichHtmlForEditor(this.modelValue)
    this.editorHtml = prepared
    this.sourceHtml = normalizeRichHtmlBody(this.modelValue)
    if (prepared !== (this.modelValue || '')) {
      this.$emit('update:modelValue', prepared)
    }
    this.$nextTick(() => {
      this.visualReady = true
    })
  },
  beforeUnmount() {
    this.visualReady = false
    this.destroyTiny()
  },
  methods: {
    buildTinyInit() {
      return {
        height: 420,
        menubar: false,
        branding: false,
        promotion: false,
        statusbar: true,
        resize: true,
        auto_focus: false,
        language: 'zh_CN',
        language_url: '/tinymce/langs/zh_CN.js',
        placeholder: this.placeholder,
        image_dimensions: false,
        object_resizing: 'img',
        plugins: [
          'lists',
          'advlist',
          'link',
          'image',
          'table',
          'code',
          'fullscreen',
          'paste',
          'charmap',
          'searchreplace',
          'wordcount',
          'visualblocks',
          'preview',
          'anchor',
          'autolink',
        ].join(' '),
        toolbar: [
          'undo redo | blocks fontsize fontfamily | bold italic underline strikethrough | forecolor backcolor',
          'alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image table charmap anchor',
          'code preview searchreplace visualblocks fullscreen',
        ],
        toolbar_mode: 'wrap',
        font_size_formats: '12px 14px 16px 18px 20px 24px 28px 32px 36px 48px',
        font_family_formats: [
          '宋体=SimSun,serif',
          '仿宋=FangSong,serif',
          '楷体=KaiTi,serif',
          '微软雅黑=Microsoft YaHei,Helvetica,sans-serif',
          '黑体=SimHei,sans-serif',
          '华文宋体=STSong,serif',
          '华文楷体=STKaiti,serif',
          '华文黑体=STHeiti,sans-serif',
          '华文仿宋=STFangsong,serif',
          '苹方=PingFang SC,Microsoft YaHei,sans-serif',
          'Helvetica=Helvetica,Arial,sans-serif',
          'Arial=Arial,Helvetica,sans-serif',
          'Times New Roman=Times New Roman,Times,serif',
          'Georgia=Georgia,serif',
          'Courier New=Courier New,Courier,monospace',
        ].join(';'),
        block_formats: '段落=p;标题1=h1;标题2=h2;标题3=h3;引用=blockquote',
        valid_elements: '*[*]',
        extended_valid_elements: '*[*]',
        verify_html: false,
        entity_encoding: 'raw',
        paste_webkit_styles: 'all',
        paste_merge_formats: true,
        convert_urls: false,
        content_style:
          'body { font-family: SimSun, "Microsoft YaHei", serif; font-size: 14px; line-height: 1.7; color: #333; } img { max-width: 100% !important; width: auto !important; height: auto !important; display: block; box-sizing: border-box; }',
        paste_preprocess: (_editor, args) => {
          args.content = normalizeRichHtmlImages(normalizeRichHtmlBody(args.content || ''))
        },
        images_upload_handler: (blobInfo) => this.uploadEditorImage(blobInfo),
        setup: (editor) => {
          const blockWheelBubble = (e) => {
            if (e.target?.closest?.('.tox-tinymce-aux')) {
              e.stopPropagation()
            }
          }
          document.addEventListener('wheel', blockWheelBubble, { capture: true })
          editor.on('remove', () => {
            document.removeEventListener('wheel', blockWheelBubble, { capture: true })
          })
        },
      }
    },
    toEditorAssetUrl(url) {
      const u = String(url || '').trim()
      if (!u) return ''
      if (/^https?:\/\//i.test(u) || u.startsWith('data:')) return u
      const base = String(BASE_URL || '').replace(/\/$/, '')
      if (base) return `${base}${u.startsWith('/') ? u : `/${u}`}`
      if (typeof window !== 'undefined' && window.location?.origin) {
        return `${window.location.origin}${u.startsWith('/') ? u : `/${u}`}`
      }
      return u
    },
    uploadEditorImage(blobInfo) {
      return uploadImage(blobInfo.blob()).then((data) => {
        const url = this.toEditorAssetUrl(data?.url || '')
        if (!url) throw new Error('empty upload url')
        return url
      })
    },
    destroyTiny() {
      const editor = this.tinyRef
      this.tinyRef = null
      this.tinyBootstrapped = false
      if (editor && typeof editor.destroy === 'function') {
        try {
          if (!editor.destroyed) editor.destroy()
        } catch {
          /* ignore teardown race */
        }
      }
      if (typeof window !== 'undefined' && window.tinymce?.get) {
        const inst = window.tinymce.get(this.editorUid)
        if (inst) {
          try {
            inst.remove()
          } catch {
            /* ignore */
          }
        }
      }
    },
    async remountVisualEditor(html) {
      this.visualReady = false
      this.destroyTiny()
      await this.$nextTick()
      this.editorUid = createEditorUid()
      this.editorMountKey += 1
      this.editorHtml = html
      this.tinyBootstrapped = false
      await this.$nextTick()
      this.visualReady = true
    },
    onTinyInit(_evt, editor) {
      this.tinyRef = editor
      if (this.tinyBootstrapped) return
      this.tinyBootstrapped = true
      const prepared = normalizeRichHtmlForEditor(this.editorHtml || this.modelValue)
      if (prepared && prepared !== editor.getContent()) {
        editor.setContent(prepared)
      }
      this.editorHtml = prepared || editor.getContent()
    },
    onTinyUpdate(html) {
      const normalized = normalizeRichHtmlImages(html)
      this.editorHtml = normalized
      this.$emit('update:modelValue', normalized)
    },
    async setEditMode(mode) {
      if (mode === this.editMode) return

      if (mode === 'source') {
        let latest = this.modelValue || ''
        if (this.tinyRef && !this.tinyRef.destroyed) {
          latest = this.tinyRef.getContent()
        }
        this.sourceHtml = normalizeRichHtmlBody(latest)
        this.$emit('update:modelValue', this.sourceHtml)
        this.visualReady = false
        this.destroyTiny()
        await this.$nextTick()
        this.editMode = 'source'
        return
      }

      const html = normalizeRichHtmlBody(this.sourceHtml || this.modelValue || '')
      this.sourceHtml = html
      this.$emit('update:modelValue', html)
      this.editMode = 'visual'
      await this.remountVisualEditor(html)
    },
    onSourceInput() {
      this.$emit('update:modelValue', this.sourceHtml)
    },
  },
}
</script>

<style scoped>
.rich-editor {
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  min-height: 400px;
}
.rich-editor-modebar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 8px 10px;
  border-bottom: 1px solid #eef2f7;
  background: #fbfcfe;
}
.mode-btn {
  border: 1px solid #d0d7e2;
  background: #fff;
  color: #344054;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
}
.mode-btn.active {
  border-color: #9bb7ff;
  background: #f5f8ff;
  color: #24438d;
}
.mode-hint {
  font-size: 12px;
  color: #667085;
}
.rich-source {
  flex: 1;
  min-height: 420px;
  border: none;
  resize: vertical;
  padding: 12px;
  font-family: Consolas, 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.55;
  color: #101828;
  background: #fff;
  box-sizing: border-box;
}
.rich-source:focus {
  outline: none;
}
.rich-editor-loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 420px;
  font-size: 13px;
  color: #98a2b3;
  background: #fbfcfe;
}
.rich-editor :deep(.tox-tinymce) {
  border: none;
  border-radius: 0;
}
.rich-editor :deep(.tox-editor-header) {
  border-bottom: 1px solid #eef2f7;
}
.rich-editor.disabled {
  opacity: 0.7;
  pointer-events: none;
}
</style>

<!-- TinyMCE 下拉挂到 body，需全局样式 -->
<style>
.tox.tox-tinymce-aux .tox-menu,
.tox.tox-tinymce-aux .tox-collection--list {
  max-height: min(280px, 42vh) !important;
  overflow-y: auto !important;
  overscroll-behavior: contain;
}
.tox.tox-tinymce-aux {
  z-index: 4000 !important;
}
</style>
